import { useEffect, useState } from 'react';
import { Archive, AlertCircle, TrendingDown, RefreshCw, Loader2, Search, X, Pencil, Plus, Minus, MapPin } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// ─────────────────────────────────────────────────────────────────────────────
// NumPad
// ─────────────────────────────────────────────────────────────────────────────

function NumPad({ value, onChange }) {
  function press(key) {
    if (key === 'C') return onChange('');
    if (key === '⌫') {
      return onChange(prev => (prev.length > 1 ? prev.slice(0, -1) : ''));
    }
    if (value === '0') return onChange(key);   // replace leading zero
    if (value.length >= 6) return;
    onChange(prev => prev + key);
  }

  const keys = ['7', '8', '9', '4', '5', '6', '1', '2', '3', 'C', '0', '⌫'];

  return (
    <div className="grid grid-cols-3 gap-2 mt-3">
      {keys.map(k => (
        <button
          key={k}
          type="button"
          onClick={() => press(k)}
          className={`
            h-12 rounded-xl text-lg font-montserrat font-bold transition-all active:scale-95
            ${k === 'C'
              ? 'bg-red-50 text-red-500 border border-red-200 hover:bg-red-100'
              : k === '⌫'
              ? 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
              : 'bg-white text-gray-800 border border-gray-200 hover:bg-gray-50 hover:border-brand-primary'}
          `}
        >
          {k}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EditQuantityModal
//
// Receives a specific `location` object:
//   { location_id, location_name, quantity }
// plus the parent `product` for display (title, sku, image).
// Sends product_id + location_id so the backend updates only that row.
// ─────────────────────────────────────────────────────────────────────────────

function EditQuantityModal({ product, location, onClose, onSaved, userId }) {
  const [tab, setTab] = useState('set');    // 'set' | 'adjust'
  const [input, setInput] = useState('');
  const [sign, setSign] = useState('+');   // only relevant for 'adjust'
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const currentQty = location.quantity ?? 0;
  const numericInput = parseInt(input || '0', 10);

  const preview = tab === 'set'
    ? numericInput
    : Math.max(0, currentQty + (sign === '+' ? numericInput : -numericInput));

  function handleChange(valOrUpdater) {
    setError('');
    setInput(prev =>
      typeof valOrUpdater === 'function' ? valOrUpdater(prev) : valOrUpdater
    );
  }

  async function handleSave() {
    if (!input || input === '0') {
      setError('Ingresa una cantidad válida.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const quantity = tab === 'adjust'
        ? (sign === '+' ? numericInput : -numericInput)
        : numericInput;

      await axios.post(`${API_URL}/api/inventory/update-quantity`, {
        product_id: product.product_id,
        location_id: location.location_id,
        quantity,
        mode: tab === 'set' ? 'set' : 'adjust',
        user_id: userId,
      });

      onSaved();
    } catch (err) {
      setError('Error al actualizar. Intenta de nuevo.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <div className="flex items-center gap-3">
            {product.primary_image ? (
              <img
                src={product.primary_image}
                alt={product.product_title}
                className="w-9 h-9 rounded-lg object-cover bg-gray-100 shrink-0"
              />
            ) : (
              <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                <Archive size={14} className="text-gray-300" />
              </div>
            )}
            <div>
              <p className="font-montserrat font-bold text-sm text-gray-800 leading-tight line-clamp-1">
                {product.product_title}
              </p>
              <p className="font-montserrat text-xs text-gray-400 font-mono">{product.sku}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* ── Location + current qty ── */}
        <div className="px-5 pb-3 flex items-center gap-3">
          <span className="flex items-center gap-1 text-xs font-montserrat font-semibold text-brand-primary bg-pink-50 border border-pink-200 px-2.5 py-1 rounded-full">
            <MapPin size={11} />
            {location.location_name}
          </span>
          <span className="text-xs font-montserrat text-gray-500">
            Stock actual:&nbsp;
            <span className="font-bold text-gray-700">{currentQty} uds</span>
          </span>
        </div>

        {/* ── Tabs ── */}
        <div className="flex mx-5 bg-gray-100 rounded-xl p-1 gap-1">
          {[{ id: 'set', label: 'Establecer' }, { id: 'adjust', label: 'Ajustar' }].map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => { setTab(t.id); setInput(''); setError(''); }}
              className={`flex-1 py-2 rounded-lg text-sm font-montserrat font-semibold transition-all ${
                tab === t.id
                  ? 'bg-white text-brand-primary shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="px-5 pb-5 pt-3 space-y-3">
          {/* ── Adjust sign toggle ── */}
          {tab === 'adjust' && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSign('+')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl border text-sm font-montserrat font-bold transition-all ${
                  sign === '+'
                    ? 'bg-green-50 border-green-300 text-green-700'
                    : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'
                }`}
              >
                <Plus size={15} /> Agregar
              </button>
              <button
                type="button"
                onClick={() => setSign('-')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl border text-sm font-montserrat font-bold transition-all ${
                  sign === '-'
                    ? 'bg-red-50 border-red-300 text-red-700'
                    : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'
                }`}
              >
                <Minus size={15} /> Restar
              </button>
            </div>
          )}

          {/* ── Input display ── */}
          <div className={`flex items-center justify-center rounded-xl border-2 py-3 transition-colors ${
            input ? 'border-brand-primary bg-pink-50/30' : 'border-gray-200 bg-gray-50'
          }`}>
            {tab === 'adjust' && input && (
              <span className={`font-montserrat font-extrabold text-2xl mr-1 ${
                sign === '+' ? 'text-green-600' : 'text-red-500'
              }`}>
                {sign}
              </span>
            )}
            <span className={`font-montserrat font-extrabold text-3xl ${
              input ? 'text-gray-800' : 'text-gray-300'
            }`}>
              {input || '0'}
            </span>
            <span className="font-montserrat text-gray-400 text-sm ml-1.5 self-end mb-1">uds</span>
          </div>

          {/* ── Preview ── */}
          {input && (
            <div className={`text-center text-xs font-montserrat font-semibold rounded-lg py-1.5 ${
              tab === 'set'
                ? 'bg-blue-50 text-blue-700'
                : sign === '+'
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-600'
            }`}>
              {tab === 'set'
                ? `Nuevo stock: ${preview} uds`
                : `${currentQty} → ${preview} uds`}
            </div>
          )}

          {/* ── Numpad ── */}
          <NumPad value={input} onChange={handleChange} />

          {/* ── Error ── */}
          {error && (
            <p className="text-xs font-montserrat text-red-500 text-center">{error}</p>
          )}

          {/* ── Save ── */}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !input}
            className="w-full py-3 rounded-xl bg-brand-primary text-white font-montserrat font-bold text-sm transition-all hover:bg-brand-primary/90 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] mt-1"
          >
            {saving ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 size={15} className="animate-spin" /> Guardando...
              </span>
            ) : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// InventoryView
//
// Flattens inventory into one row per (product × location) so each
// location's stock is independently visible and editable.
//
// @param {Object}  dashboardData  shared data & fetchers from usePackageDashboard
// @param {number}  userId         logged-in admin user ID (passed from PackageDashboard)
// ─────────────────────────────────────────────────────────────────────────────

export function InventoryView({ dashboardData, userId }) {
  const { inventory, inventorySummary, inventoryLoading, inventoryError, fetchInventory } = dashboardData;

  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // editingTarget = { product, location } | null
  const [editingTarget, setEditingTarget] = useState(null);

  useEffect(() => {
    fetchInventory(lowStockOnly);
  }, [lowStockOnly, fetchInventory]);

  // ── Flatten: one row per (product × location) ────────────────────────────
  const flatRows = inventory.flatMap(product =>
    product.locations.map(loc => ({ product, location: loc }))
  );

  const filteredRows = flatRows.filter(({ product }) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      product.product_title?.toLowerCase().includes(term) ||
      product.sku?.toLowerCase().includes(term)
    );
  });

  function handleSaved() {
    setEditingTarget(null);
    fetchInventory(lowStockOnly);
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">

        {/* ── Header ── */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-montserrat font-extrabold text-xl text-gray-800">
              Inventario
            </h2>
            {inventorySummary && (
              <p className="font-montserrat text-sm text-gray-500 mt-0.5">
                {inventorySummary.total_products} productos ·{' '}
                <span className={inventorySummary.low_stock_count > 0 ? 'text-amber-600 font-semibold' : ''}>
                  {inventorySummary.low_stock_count} con stock bajo
                </span>
                {inventorySummary.out_of_stock_count > 0 && (
                  <span className="text-red-600 font-semibold">
                    {' '}· {inventorySummary.out_of_stock_count} sin stock
                  </span>
                )}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setLowStockOnly(prev => !prev)}
              className={`flex items-center gap-2 text-sm font-montserrat font-semibold px-3 py-2 rounded-lg border transition-colors ${
                lowStockOnly
                  ? 'bg-amber-50 border-amber-300 text-amber-700'
                  : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              <TrendingDown size={15} />
              Stock bajo
            </button>

            <button
              onClick={() => fetchInventory(lowStockOnly)}
              disabled={inventoryLoading}
              className="flex items-center gap-2 text-sm font-montserrat font-semibold text-gray-500 hover:text-brand-primary transition-colors disabled:opacity-50 px-3 py-2 rounded-lg border border-gray-200 hover:border-gray-300"
            >
              <RefreshCw size={15} className={inventoryLoading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* ── Search ── */}
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Buscar producto o SKU..."
            className="w-full pl-9 pr-8 py-2.5 text-sm font-montserrat bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-brand-primary transition-colors"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* ── Error ── */}
        {inventoryError && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm font-montserrat text-red-600">
            {inventoryError}
          </div>
        )}

        {/* ── Table ── */}
        {inventoryLoading ? (
          <div className="flex items-center justify-center py-24 gap-3">
            <Loader2 size={24} className="animate-spin text-brand-primary" />
            <span className="font-montserrat text-gray-400 text-sm">Cargando inventario...</span>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <Archive size={40} className="text-gray-200" />
            <p className="font-montserrat text-gray-400 text-sm">
              {lowStockOnly ? 'Sin productos con stock bajo' : 'Sin productos en inventario'}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <Th>Producto</Th>
                  <Th>SKU</Th>
                  <Th>Precio</Th>
                  <Th>Ubicación</Th>
                  <Th>Stock</Th>
                  <Th>Estado</Th>
                  <Th>Editar</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRows.map(({ product, location }) => {
                  const isOut = location.quantity === 0;
                  const isLow = location.is_low_stock && !isOut;
                  const rowKey = `${product.product_id}-${location.location_id}`;

                  return (
                    <tr
                      key={rowKey}
                      className={
                        isOut ? 'bg-red-50/40' :
                        isLow ? 'bg-amber-50/40' :
                        'hover:bg-gray-50/60'
                      }
                    >
                      {/* Product */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {product.primary_image ? (
                            <img
                              src={product.primary_image}
                              alt={product.product_title}
                              className="w-10 h-10 rounded-lg object-cover bg-gray-100 shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                              <Archive size={16} className="text-gray-300" />
                            </div>
                          )}
                          <span className="font-montserrat font-semibold text-sm text-gray-800">
                            {product.product_title}
                          </span>
                        </div>
                      </td>

                      {/* SKU */}
                      <td className="px-4 py-3">
                        <span className="font-montserrat text-xs text-gray-400 font-mono bg-gray-100 px-2 py-0.5 rounded">
                          {product.sku}
                        </span>
                      </td>

                      {/* Price */}
                      <td className="px-4 py-3">
                        <span className="font-montserrat text-sm text-gray-600">
                          S/ {product.price?.toFixed(2)}
                        </span>
                      </td>

                      {/* Location pill */}
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1.5 text-xs font-montserrat font-semibold text-brand-primary bg-pink-50 border border-pink-200 px-2.5 py-1 rounded-full w-fit">
                          <MapPin size={11} />
                          {location.location_name}
                        </span>
                      </td>

                      {/* Stock for this location */}
                      <td className="px-4 py-3">
                        <span className={`font-montserrat font-extrabold text-lg ${
                          isOut ? 'text-red-600' : isLow ? 'text-amber-600' : 'text-gray-800'
                        }`}>
                          {location.quantity}
                        </span>
                        <span className="font-montserrat text-xs text-gray-400 ml-1">uds</span>
                      </td>

                      {/* Status badge */}
                      <td className="px-4 py-3">
                        {isOut ? (
                          <span className="flex items-center gap-1 text-xs font-montserrat font-bold text-red-600 bg-red-100 px-2.5 py-1 rounded-full w-fit">
                            <AlertCircle size={11} />
                            Sin stock
                          </span>
                        ) : isLow ? (
                          <span className="flex items-center gap-1 text-xs font-montserrat font-bold text-amber-600 bg-amber-100 px-2.5 py-1 rounded-full w-fit">
                            <TrendingDown size={11} />
                            Stock bajo
                          </span>
                        ) : (
                          <span className="text-xs font-montserrat text-green-600 bg-green-50 px-2.5 py-1 rounded-full w-fit block">
                            ✓ Saludable
                          </span>
                        )}
                      </td>

                      {/* Edit — opens modal scoped to this specific location */}
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setEditingTarget({ product, location })}
                          className="p-2 rounded-lg border border-gray-200 text-gray-400 hover:text-brand-primary hover:border-brand-primary transition-colors"
                          title={`Editar stock en ${location.location_name}`}
                        >
                          <Pencil size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit modal — scoped to one specific location */}
      {editingTarget && (
        <EditQuantityModal
          product={editingTarget.product}
          location={editingTarget.location}
          onClose={() => setEditingTarget(null)}
          onSaved={handleSaved}
          userId={userId}
        />
      )}
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-montserrat font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">
      {children}
    </th>
  );
}